import React, { useState, useEffect, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { SortingState, ColumnFiltersState } from "@tanstack/react-table";
import { toast } from "sonner";
import { Plus, Loader2, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAuthStore } from "@/app/store/authStore";
import { Link } from "react-router-dom";
import { PaginatedResponse, ListQueryParams } from "@/app/types/api";
import { HsnCode, HsnCodeCreate, HsnCodeUpdate } from "@/app/types/hsnCode";
import { listHsnCodes, createHsnCode, updateHsnCode, deleteHsnCode } from "@/app/services/hsnCodeService";
import HsnCodeDataTable from "./components/HsnCodeDataTable";
import HsnCodeForm from "./components/HsnCodeForm";
import { columns as hsnColumns } from "./components/columns";
import { AuthState } from "@/app/store/authStore";
import { User } from "@/app/types/auth";

// Define selectors for auth store
const selectUser = (state: AuthState): User | null => state.user;
const selectLogoutAction = (state: AuthState): (() => void) => state.logoutAction;
const selectIsLoading = (state: AuthState): boolean => state.isLoading;

const HsnCodesPage: React.FC = () => {
  const queryClient = useQueryClient();
  
  // Local State
  const [isCreateFormOpen, setIsCreateFormOpen] = useState(false);
  const [isEditFormOpen, setIsEditFormOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [currentHsnCode, setCurrentHsnCode] = useState<HsnCode | null>(null);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [searchValue, setSearchValue] = useState("");
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
    pageCount: 0,
  });
  
  // Auth state
  const user = useAuthStore(selectUser);
  const logoutAction = useAuthStore(selectLogoutAction);
  const isAuthLoading = useAuthStore(selectIsLoading);
  
  // Get organization ID from user data
  const userOrgId = useMemo(() => user?.organization_id || user?.organization?.id, [user]);
  const isSuperAdmin = useMemo(() => user?.role?.name === 'super_admin', [user]);
  const isOrgAdmin = useMemo(() => user?.role?.name === 'org_admin', [user]);
  const canManageHsnCodes = useMemo(() => isSuperAdmin || isOrgAdmin, [isSuperAdmin, isOrgAdmin]);
  
  // Construct query parameters for the API
  const queryParams = useMemo<ListQueryParams>(() => {
    const params: ListQueryParams = {
      skip: pagination.pageIndex * pagination.pageSize,
      limit: pagination.pageSize,
      search_query: searchValue || undefined,
    };
    
    // Add sorting if defined
    if (sorting.length > 0) {
      params.sort_by = sorting[0].id;
      params.sort_dir = sorting[0].desc ? "desc" : "asc";
    }
    
    // Add filters if defined
    if (columnFilters.length > 0) {
      const filters: { [key: string]: any } = {};
      columnFilters.forEach((filter) => {
        filters[filter.id] = filter.value;
      });
      params.filters = JSON.stringify(filters);
    }
    
    return params;
  }, [pagination.pageIndex, pagination.pageSize, searchValue, sorting, columnFilters]);
  
  // Query to fetch HSN codes
  const { 
    data: hsnCodesResponse = { items: [], total: 0 },
    isLoading: isHsnCodesLoading, 
    isError: isHsnCodesError,
    error: hsnCodesError,
    refetch: refetchHsnCodes,
  } = useQuery<PaginatedResponse<HsnCode>>({
    queryKey: ['hsncodes', queryParams],
    queryFn: () => listHsnCodes(queryParams),
    enabled: true, // HSN codes are global and can be loaded for all users
  });
  
  // Update pagination when total changes
  useEffect(() => {
    if (hsnCodesResponse) {
      setPagination(prev => ({
        ...prev,
        pageCount: Math.ceil(hsnCodesResponse.total / prev.pageSize),
      }));
    }
  }, [hsnCodesResponse]);

  // Setup event listeners for edit/delete actions from the table
  useEffect(() => {
    const handleEditHsnCode = (event: CustomEvent<HsnCode>): void => {
      setCurrentHsnCode(event.detail);
      setIsEditFormOpen(true);
    };

    const handleDeleteHsnCode = (event: CustomEvent<HsnCode>): void => {
      setCurrentHsnCode(event.detail);
      setIsDeleteDialogOpen(true);
    };

    document.addEventListener('edit-hsncode', handleEditHsnCode as EventListener);
    document.addEventListener('delete-hsncode', handleDeleteHsnCode as EventListener);

    return () => {
      document.removeEventListener('edit-hsncode', handleEditHsnCode as EventListener);
      document.removeEventListener('delete-hsncode', handleDeleteHsnCode as EventListener);
    };
  }, []);
  
  // Mutations
  const createMutation = useMutation({
    mutationFn: (data: HsnCodeCreate) => createHsnCode(data),
    onSuccess: () => {
      toast.success('HSN code created successfully');
      queryClient.invalidateQueries({ queryKey: ['hsncodes'] });
      setIsCreateFormOpen(false);
    },
    onError: (error) => {
      toast.error(`Failed to create HSN code: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: HsnCodeUpdate }) => updateHsnCode(id, data),
    onSuccess: () => {
      toast.success('HSN code updated successfully');
      queryClient.invalidateQueries({ queryKey: ['hsncodes'] });
      setIsEditFormOpen(false);
      setCurrentHsnCode(null);
    },
    onError: (error) => {
      toast.error(`Failed to update HSN code: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteHsnCode(id),
    onSuccess: () => {
      toast.success('HSN code deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['hsncodes'] });
      setIsDeleteDialogOpen(false);
      setCurrentHsnCode(null);
    },
    onError: (error) => {
      toast.error(`Failed to delete HSN code: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  });
  
  // Event Handlers
  const handleCreateClick = (): void => {
    setIsCreateFormOpen(true);
  };

  const handleCreateSubmit = (formData: HsnCodeCreate): void => {
    createMutation.mutate(formData);
  };

  const handleEditSubmit = (formData: HsnCodeUpdate): void => {
    if (currentHsnCode) {
      updateMutation.mutate({ id: currentHsnCode.id, data: formData });
    }
  };

  const handleDeleteConfirm = (): void => {
    if (currentHsnCode) {
      deleteMutation.mutate(currentHsnCode.id);
    }
  };
  
  const handlePaginationChange = (newPagination: { pageIndex: number; pageSize: number }): void => {
    setPagination(prev => ({
      ...prev,
      pageIndex: newPagination.pageIndex,
      pageSize: newPagination.pageSize,
    }));
  };
  
  // --- Render Logic ---
  
  // Show loading indicator if auth state is loading
  if (isAuthLoading) {
    return (
      <div className="flex justify-center items-center min-h-[50vh]">
        <Loader2 className="h-10 w-10 animate-spin text-primary" />
      </div>
    );
  }
  
  // Handle error state
  if (isHsnCodesError) {
    return (
      <div className="py-8 flex flex-col items-center justify-center space-y-4 text-center">
        <AlertTriangle className="h-12 w-12 text-destructive" />
        <p className="text-xl font-semibold text-destructive">Error Loading HSN Codes</p>
        <p className="text-sm text-muted-foreground">
          {hsnCodesError instanceof Error ? hsnCodesError.message : 'An unknown error occurred.'}
        </p>
        <Button onClick={() => refetchHsnCodes()} variant="outline">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Retry
        </Button>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold tracking-tight">HSN Codes</h1>
        {canManageHsnCodes && (
          <Button onClick={handleCreateClick}>
            <Plus className="mr-2 h-4 w-4" /> Add HSN Code
          </Button>
        )}
      </div>
      <p className="text-muted-foreground">
        Harmonized System of Nomenclature (HSN) codes for goods and services, used for tax classification.
      </p>
      
      {/* HSN Codes Data Table */}
      <HsnCodeDataTable
        data={hsnCodesResponse}
        columns={hsnColumns}
        isLoading={isHsnCodesLoading}
        isSuperAdmin={isSuperAdmin}
        pagination={pagination}
        sorting={sorting}
        columnFilters={columnFilters}
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        onPaginationChange={handlePaginationChange}
        onSortingChange={setSorting}
        onColumnFiltersChange={setColumnFilters}
      />
      
      {/* Create Form */}
      {canManageHsnCodes && (
        <HsnCodeForm
          open={isCreateFormOpen}
          onOpenChange={setIsCreateFormOpen}
          onSubmit={handleCreateSubmit}
          isLoading={createMutation.isPending}
          mode="create"
        />
      )}
      
      {/* Edit Form */}
      {currentHsnCode && canManageHsnCodes && (
        <HsnCodeForm
          open={isEditFormOpen}
          onOpenChange={(open) => {
            setIsEditFormOpen(open);
            if (!open) setCurrentHsnCode(null);
          }}
          onSubmit={handleEditSubmit}
          defaultValues={currentHsnCode}
          isLoading={updateMutation.isPending}
          mode="edit"
        />
      )}
      
      {/* Delete Confirmation */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the HSN code &quot;{currentHsnCode?.code}&quot;.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteConfirm} 
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default HsnCodesPage; 